import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import { Avatar, Paragraph, Stars, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

import { CompetencyDisplayWithValue } from 'packages/kakapo-ui-application/common/containers/CompetencyDisplay';
import VoiceMemoPlayback from 'voice-memos/VoiceMemoPlayback';
import { CommentsWithData } from 'comments/Comments';
import CommentBar from 'comments/components/CommentBar';
import { useState } from 'react';

const DateLabel = styled(Paragraph)`
	opacity: 0;
	transition: 250ms opacity ease-out;
`;

const Container = styled(Flex)`
	page-break-inside: avoid;

	:hover {
		${DateLabel} {
			opacity: 1;
		}
	}
`;

const Bubble = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	border-radius: 5px;

	forced-color-adjust: exact;
	print-color-adjust: exact;
	-webkit-print-color-adjust: exact;
`;

export default function FeedbackResultAnswer(props: {
	response: graphql.FeedbackResponse;
	recipient: graphql.FeedbackRecipient;
	question: graphql.FeedbackRequestQuestion;
	answer: graphql.FeedbackResponseAnswer;
}) {
	const [composer, setComposer] = useState(false);

	const metadata = { questionID: props.question.id };

	return (
		<Container>
			<Avatar user={props.recipient} size={32} flexGrow={0} flexShrink={0} />
			<Box ml={2} flexGrow={1} flexShrink={1}>
				<CommentBar.Container
					onComment={() => setComposer((composer) => !composer)}
					targetID={props.response.id}
					targetType={graphql.CommentTargetType.FeedbackResponse}
					metadata={metadata}
					enabled={props.response.recipient.__typename === 'User'}
				>
					<Bubble p={3} mb={3}>
						<Flex alignItems="center" justifyContent="space-between">
							<Subheading>
								{props.recipient.firstName} {props.recipient.lastName}
							</Subheading>
							<DateLabel>{moment(props.response.createdAt).format('LL')}</DateLabel>
						</Flex>
						{props.answer.text && (
							<Paragraph mt={2}>
								<QuillText quill={props.answer.text} />
							</Paragraph>
						)}
						{typeof props.answer.stars === 'number' && <Stars mt={2} stars={props.answer.stars} />}
						{props.question.competency && typeof props.answer.competencyLevelIndex === 'number' && (
							<Box mt={2}>
								<CompetencyDisplayWithValue
									levels={props.question.competency.levels}
									manager={props.answer.competencyLevelIndex}
								/>
							</Box>
						)}
						{props.answer.voiceMemo && (
							<Box py={3}>
								<VoiceMemoPlayback
									samples={props.answer.voiceMemo.samples}
									source={props.answer.voiceMemo.asset.source.url}
								/>
							</Box>
						)}
					</Bubble>
				</CommentBar.Container>

				{props.response.recipient.__typename === 'User' && (
					<CommentsWithData
						empty={false}
						composer={composer}
						comments={props.response.comments}
						autoFocus
						target={props.response}
						metadata={metadata}
						onCancelComposer={() => setComposer(false)}
						onRefetch={() => {}}
					/>
				)}
			</Box>
		</Container>
	);
}

function FeedbackResultAnswerSkeleton() {
	return (
		<Container>
			<Avatar.Skeleton size={32} flexGrow={0} flexShrink={0} />
			<Bubble ml={2} flexGrow={1} flexShrink={1} p={3}>
				<Subheading.Skeleton />
				<Paragraph.Skeleton mt={2} />
			</Bubble>
		</Container>
	);
}

FeedbackResultAnswer.Skeleton = FeedbackResultAnswerSkeleton;
